import { Component, computed, inject } from '@angular/core';
import { ContentService } from '@content/content.service';
import { EventService } from '@event/event.service';
import { FolloweeService } from '@followee/followee.service';
import {
  IonBadge,
  IonIcon,
  IonLabel,
  IonTabBar,
  IonTabButton,
  IonTabs
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationService } from '@notification/notification.service';
import { addIcons } from 'ionicons';
import {
  albums,
  bicycle,
  home,
  homeOutline,
  informationCircle,
  newspaper,
  people,
} from 'ionicons/icons';

@Component({
  selector: 'ad6-tabs',
  templateUrl: 'tabs.page.html',
  styleUrl: 'tabs.page.scss',
  imports: [
    IonBadge,
    IonIcon,
    IonLabel,
    IonTabs,
    IonTabButton,
    IonTabBar,
    TranslateModule,
  ],
})
export class TabsPage {
  private readonly eventService = inject(EventService);
  private readonly notificationService = inject(NotificationService);
  private readonly contentService = inject(ContentService);
  private readonly followeeService = inject(FolloweeService);

  private isEventLive = computed<boolean>(
    () => this.eventService.event()?.isEventLive ?? false,
  );

  private showParticipants = computed<boolean>(
    () => this.eventService.event()?.showParticipants ?? false,
  );

  /**
   * Tab 'content' is shown based on available content news items
   */
  showContent = computed<boolean>(() =>
    this.contentService.hasContentNewsItems(),
  );

  /**
   * Tab 'information' is shown based on available content categories
   */
  showInformation = computed<boolean>(
    () => this.contentService.hasContentCategories(),
  );

  /**
   * Tab 'live' / 'participants' is shown when the event is live or participant data is available
   * Is the event is live, the tab is called "live"
   * If only participants data is available the tab is called "participants"
   * If event is not live and no data is available the tab is not shown
   */
  liveTab = computed<'live' | 'participants' | undefined>(() =>
    this.isEventLive()
      ? 'live'
      : this.showParticipants()
        ? 'participants'
        : undefined,
  );

  /**
   * The icon for the live tab is based on the value of liveTab
   * When live it is a bicycle, otherwise it is the people icon
   */
  liveTabIcon = computed<'bicycle' | 'people'>(() =>
    this.liveTab() === 'live' ? 'bicycle' : 'people',
  );

  /**
   * The number of unread notifications is shown as badge on the start tab
   */
  unreadNotifications = computed<number>(
    () => this.notificationService.unreadNotifications().length,
  );

  unreadFollowees = computed<number>(() =>
    this.followeeService.unreadFollowees(),
  );

  constructor() {
    addIcons({
      bicycle,
      homeOutline,
      home,
      newspaper,
      people,
      albums,
      informationCircle,
    });
  }

  /** TODO: @deprecated? */
  // ionViewDidEnter(): void {
  // this.backSubscription = this.platform.backButton.subscribeWithPriority(
  //   -1,
  //   async () => {
  //     const activeTab = this.localIonTabs()!.getSelected();
  //     console.log(`BACK - On tab ${activeTab}`);
  //     if (this.localIonTabs()!.outlet) {
  //       if (this.localIonTabs()!.outlet.canGoBack()) {
  //         console.log(`On tab ${activeTab}, go back...`);
  //         await this.localIonTabs()!.outlet.pop();
  //         return;
  //       }
  //       if (activeTab === 'ad6') {
  //         // From the ad6 tab, always minimize the app on back button if there is no history
  //         console.log('On home page and cannot go back. Minimize app...');
  //         App.minimizeApp();
  //       } else {
  //         console.log('On other tab, navigate back...');
  //         this.navController.back();
  //       }
  //     }
  //   },
  // );
  // }
}
